import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Check, X, Trash2, Eye, Plus, MapPin,
  Phone, Mail, Clock, Users, ArrowLeft, Search,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approuvée', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejetée', color: 'bg-red-100 text-red-800' },
};

const CLINIC_TYPES = [
  'Clinique privée', 'Cabinet de groupe', 'Centre dentaire',
  'Centre médical', 'Hôpital privé', 'Polyclinique',
];

const AdminClinics = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [pendingClinics, setPendingClinics] = useState([]);
  const [allClinics, setAllClinics] = useState([]);
  const [wilayas, setWilayas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const getAuthConfig = () => {
    // Correctif B7 : le cookie httpOnly porte la session.
    return {
      withCredentials: true,
      headers: {},
    };
  };
  const [newClinic, setNewClinic] = useState({
    name: '', clinic_type: '', wilaya_code: '', address: '',
    phone: '', email: '', description: '', specialties: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    loadData();
    axios.get(`${API_URL}/api/wilayas`).then(r => setWilayas(r.data)).catch(() => {});
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/clinics/pending`, getAuthConfig()),
        axios.get(`${API_URL}/api/admin/clinics`, getAuthConfig()),
      ]);
      setPendingClinics(pendingRes.data);
      setAllClinics(allRes.data.clinics || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (clinic) => {
    if (!window.confirm(`Approuver la clinique "${clinic.name}" ?`)) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/clinics/${clinic.clinic_id}/approve`,
        {}, getAuthConfig()
      );
      toast.success(`Clinique approuvée. Mot de passe: ${res.data.temp_password}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedClinic) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/clinics/${selectedClinic.clinic_id}/reject`,
        { reason: rejectReason }, getAuthConfig()
      );
      toast.success('Clinique rejetée');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedClinic(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (clinic) => {
    if (!window.confirm(`Supprimer définitivement "${clinic.name}" ? Cette action est irréversible.`)) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/clinics/${clinic.clinic_id}`, getAuthConfig());
      toast.success('Clinique supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivatePro = async (clinicId, currentStatus) => {
    if (!window.confirm(currentStatus ? 'Désactiver le mode Pro ?' : 'Activer le mode Pro pour cette clinique ?')) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/pro/activate`, { clinic_id: clinicId, is_pro: !currentStatus }, getAuthConfig());
      loadData();
    } catch (err) {
      alert('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const payload = {
        ...newClinic,
        specialties: newClinic.specialties.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await axios.post(
        `${API_URL}/api/admin/clinics`, payload, getAuthConfig()
      );
      toast.success(`Clinique créée. Mot de passe temporaire: ${res.data.temp_password}`);
      setCreateDialogOpen(false);
      setNewClinic({
        name: '', clinic_type: '', wilaya_code: '', address: '',
        phone: '', email: '', description: '', specialties: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAll = allClinics.filter(c =>
    !searchQuery ||
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
                <Building2 className="w-6 h-6 text-[#0596DE]" />
                Gestion des cliniques
              </h1>
              <p className="text-sm text-[#64748B]">
                Validez les demandes et gérez les établissements partenaires
              </p>
            </div>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full"
          >
            <Plus className="w-4 h-4 mr-1" /> Créer une clinique
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-[#64748B]">En attente</p>
                <p className="text-2xl font-bold text-[#0F172A]">{pendingClinics.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-[#64748B]">Approuvées</p>
                <p className="text-2xl font-bold text-[#0F172A]">
                  {allClinics.filter(c => c.status === 'approved').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-[#64748B]">Total</p>
                <p className="text-2xl font-bold text-[#0F172A]">{allClinics.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
          <TabsList className="mb-4">
            <TabsTrigger value="pending">
              En attente ({pendingClinics.length})
            </TabsTrigger>
            <TabsTrigger value="all">Toutes ({allClinics.length})</TabsTrigger>
          </TabsList>

          {/* Pending */}
          <TabsContent value="pending">
            {loading ? (
              <p className="text-center py-12 text-[#64748B]">Chargement...</p>
            ) : pendingClinics.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                <p className="text-[#64748B]">Aucune demande en attente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingClinics.map((c) => (
                  <div key={c.clinic_id} className="border border-[#E2E8F0] rounded-xl p-4 hover:bg-[#F8FAFC] transition">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-[#0F172A]">{c.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_LABELS[c.status]?.color}`}>
                            {STATUS_LABELS[c.status]?.label}
                          </span>
                          {c.establishment_type === "hospital" && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#E6F1FB] text-[#185FA5] font-medium">Hôpital</span>
                          )}
                        </div>
                        <p className="text-sm text-[#64748B] mb-2">{c.establishment_type === "hospital" ? "Etablissement hospitalier" : c.clinic_type}</p>
                        <div className="flex flex-wrap gap-3 text-sm text-[#64748B]">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {c.address}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" /> {c.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" /> {c.email}
                          </span>
                        </div>
                        {c.specialties?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {c.specialties.map((s, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-[#E0F2FE] text-[#0596DE] rounded-full">
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                        {c.description && (
                          <p className="text-sm text-[#64748B] mt-2 italic">{c.description}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(c)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700 rounded-full"
                        >
                          <Check className="w-4 h-4 mr-1" /> Approuver
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setSelectedClinic(c); setRejectDialogOpen(true); }}
                          disabled={actionLoading}
                          className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-1" /> Rejeter
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All */}
          <TabsContent value="all">
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-xl"
              />
            </div>
            {loading ? (
              <p className="text-center py-12 text-[#64748B]">Chargement...</p>
            ) : filteredAll.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
                <p className="text-[#64748B]">Aucune clinique</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAll.map((c) => (
                  <div key={c.clinic_id} className="border border-[#E2E8F0] rounded-xl p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-[#0F172A]">{c.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_LABELS[c.status]?.color}`}>
                            {STATUS_LABELS[c.status]?.label}
                          </span>
                          {c.doctor_count > 0 && (
                            <span className="text-xs flex items-center gap-1 text-[#64748B]">
                              <Users className="w-3 h-3" /> {c.doctor_count} médecin(s)
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#64748B]">{c.email} · {c.phone}</p>
                        {c.slug && c.status === 'approved' && (
                          <a
                          
                            href={`/clinic/${c.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#0596DE] hover:underline mt-1 inline-block"
                          >
                            Voir page publique →
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActivatePro(c.clinic_id, c.is_pro)}
                          disabled={actionLoading}
                          className={`rounded-full text-xs ${c.is_pro ? 'bg-yellow-50 text-yellow-600 border-yellow-200' : 'bg-blue-50 text-[#0596DE] border-blue-200'}`}
                        >
                          {c.is_pro ? '⭐ Pro actif' : '⭐ Activer Pro'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(c)}
                          disabled={actionLoading}
                          className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog rejeter */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter la demande</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">
              Vous êtes sur le point de rejeter <strong>{selectedClinic?.name}</strong>
            </p>
            <Textarea
              placeholder="Raison du rejet (optionnel, sera envoyée à la clinique)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="rounded-full">
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 rounded-full"
            >
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog créer */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer une nouvelle clinique</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={newClinic.name}
                onChange={(e) => setNewClinic({ ...newClinic, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <select
                  value={newClinic.clinic_type}
                  onChange={(e) => setNewClinic({ ...newClinic, clinic_type: e.target.value })}
                  required
                  className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="">Sélectionner...</option>
                  {CLINIC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Wilaya *</Label>
                <select
                  value={newClinic.wilaya_code}
                  onChange={(e) => setNewClinic({ ...newClinic, wilaya_code: e.target.value })}
                  required
                  className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white"
                >
                  <option value="">Sélectionner...</option>
                  {wilayas.map(w => <option key={w.code} value={w.code}>{w.code} - {w.name_fr}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Adresse *</Label>
              <Input
                value={newClinic.address}
                onChange={(e) => setNewClinic({ ...newClinic, address: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={newClinic.phone}
                  onChange={(e) => setNewClinic({ ...newClinic, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newClinic.email}
                  onChange={(e) => setNewClinic({ ...newClinic, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Spécialités (séparées par virgules)</Label>
              <Input
                value={newClinic.specialties}
                onChange={(e) => setNewClinic({ ...newClinic, specialties: e.target.value })}
                placeholder="Soins dentaires, Orthodontie, Implantologie"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newClinic.description}
                onChange={(e) => setNewClinic({ ...newClinic, description: e.target.value })}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="rounded-full">
                Annuler
              </Button>
              <Button type="submit" disabled={actionLoading} className="bg-[#0596DE] hover:bg-[#047CBA] rounded-full">
                Créer la clinique
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClinics;
